import { faroCoreApiClient } from "@api/faro-core-api-client";
import { getErrorDisplayMarkup } from "@context-providers/error-boundary/error-boundary-utils";
import {
  BaseCompanyIdProps,
  BaseProjectIdProps,
} from "@custom-types/base-types";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { RootState } from "@store/store-helper";

/** Fetches stats from the backend to show analytics */
export const fetchCompanyStats = createAsyncThunk<
  FaroCoreApiTypes.CompanyStatistic[],
  BaseCompanyIdProps,
  { state: RootState }
>("groups/fetchCompanyStats", async ({ companyId }, thunkAPI) => {
  if (!companyId) {
    throw new Error("No companyId was given to fetchCompanyStats");
  }

  const state = thunkAPI.getState();
  // Caches the response of the stats not to always fetch them from the backend
  if (state.analytics.companyStats) {
    return state.analytics.companyStats;
  }

  try {
    const response = await faroCoreApiClient.company.getCompanyStatistics({
      companyId,
    });
    return response;
  } catch (error) {
    throw new Error(getErrorDisplayMarkup(error));
  }
});

interface FetchProjectStatsResponse extends BaseProjectIdProps {
  /** Stats for a particular project */
  stats: FaroCoreApiTypes.CompanyStatistic[];
}

/** Fetches stats from the backend for a single project to show analytics */
export const fetchProjectStats = createAsyncThunk<
  FetchProjectStatsResponse,
  BaseProjectIdProps & BaseCompanyIdProps,
  { state: RootState }
>("groups/fetchProjectStats", async ({ companyId, projectId }, thunkAPI) => {
  if (!companyId) {
    throw new Error("No companyId was given to fetchProjectStats");
  }

  const state = thunkAPI.getState();
  // Caches the response of the stats not to always fetch them from the backend
  if (state.analytics.projectStats[projectId]) {
    return {
      projectId,
      stats: state.analytics.projectStats[projectId],
    };
  }

  try {
    const response = await faroCoreApiClient.projects.getProjectStatistics({
      companyId,
      projectId,
    });
    return {
      projectId,
      stats: response,
    };
  } catch (error) {
    throw new Error(getErrorDisplayMarkup(error));
  }
});
